import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ViewChild, AfterViewInit, Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  WorkLogService,
  WorkLog,
  resolveWorkLogImageUrl,
} from 'src/app/shared/services/work-log.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { WorkDialogComponent } from '../work-dialog/work-dialog.component';
import { ImageViewDialogComponent } from '../image-view-dialog/image-view-dialog.component';

export interface ProjectWorkRow {
  id?: number;
  title: string;
  description: string;
  submittingOrgName: string;
  date: string;
  created_at?: string;
  dateFormatted: string;
  createdAtFormatted: string;
  hoursWorked: number;
  progressPercentage: number;
  status: string;
  imageList: string[];
  fileCount: number;
}

@Component({
  selector: 'app-add-work-project',
  templateUrl: './add-work-project.component.html',
  styleUrl: './add-work-project.component.css'
})
export class AddWorkProjectComponent implements OnInit, AfterViewInit, OnChanges {

  /** When used inside project detail (no route :id), pass the project id here. */
  @Input() embeddedProjectId: number | string | null = null;

  displayedColumns: string[] = ['srNo', 'title', 'organization', 'description', 'date', 'hours', 'progress', 'status', 'createdAt', 'images'];
  dataSource = new MatTableDataSource<ProjectWorkRow>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  projectId: any;
  private routeProjectId: any;
  loading = false;

  constructor(
    private workLogService: WorkLogService,
    private toastrMsgService: ToastrMsgService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (data: ProjectWorkRow, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case 'srNo':
          return data.id ?? 0;
        case 'title':
          return (data.title || '').toLowerCase();
        case 'organization':
          return (data.submittingOrgName || '').toLowerCase();
        case 'description':
          return (data.description || '').toLowerCase();
        case 'date':
          return data.date ? new Date(data.date).getTime() : 0;
        case 'createdAt':
          return data.created_at ? new Date(data.created_at).getTime() : 0;
        default:
          return (data as any)[sortHeaderId];
      }
    };
    this.dataSource.filterPredicate = (row: ProjectWorkRow, filter: string) => {
      const q = filter.trim().toLowerCase();
      if (!q) return true;
      const blob = [
        row.title,
        row.description,
        row.submittingOrgName,
        row.status,
        row.dateFormatted,
        row.createdAtFormatted,
        String(row.hoursWorked),
        String(row.progressPercentage),
        String(row.fileCount),
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['embeddedProjectId']) {
      this.resolveProjectAndLoad();
    }
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.routeProjectId = params['id'];
      this.resolveProjectAndLoad();
    });
  }

  private resolveProjectAndLoad(): void {
    this.projectId = this.embeddedProjectId ?? this.routeProjectId;
    if (this.projectId) {
      this.getProjetWork();
    }
  }

  applyFilter(event: Event) {
    const raw = (event.target as HTMLInputElement).value;
    this.dataSource.filter = raw.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /** Rows currently visible after client-side filter (for stats). */
  get visibleCount(): number {
    return this.dataSource.filteredData.length;
  }

  get withAttachmentsCount(): number {
    return this.dataSource.filteredData.filter((r) => r.fileCount > 0).length;
  }

  private imageUrlsFromLog(log: WorkLog): string[] {
    return (log.images || [])
      .map((img) => resolveWorkLogImageUrl(img.image_url || ''))
      .filter(Boolean);
  }

  private mapWorkLogToRow(log: WorkLog): ProjectWorkRow {
    const logDate = log.log_date ?? (log as any).logDate;
    const createdAt = log.created_at ?? (log as any).createdAt;
    const d = logDate ? new Date(logDate) : null;
    const created = createdAt ? new Date(createdAt) : null;
    const urls = this.imageUrlsFromLog(log);
    const count = urls.length || Number(log.image_count) || 0;

    return {
      id: log.id,
      title: log.title ?? '',
      description: log.description ?? '',
      submittingOrgName: log.submitting_org_name ?? '',
      date: logDate || '',
      created_at: createdAt,
      dateFormatted: d ? d.toLocaleDateString() : '—',
      createdAtFormatted: created
        ? `${created.toLocaleDateString()} ${created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
        : '—',
      hoursWorked: Number(log.hours_worked) || 0,
      progressPercentage: Number(log.progress_percentage) || 0,
      status: log.status ?? 'in_progress',
      imageList: urls,
      fileCount: count,
    };
  }

  getProjetWork() {
    if (!this.projectId) return;
    this.loading = true;

    this.workLogService
      .getWorkLogs({
        projectId: Number(this.projectId),
        limit: 500,
        page: 1,
      })
      .pipe(map((res) => (res?.data ?? []).map((log) => this.mapWorkLogToRow(log))))
      .subscribe({
        next: (rows) => {
          this.dataSource.data = rows;
        },
        error: (err) => {
          const msg =
            err?.error?.errorMessage ||
            err?.error?.message ||
            err?.message ||
            'Failed to load work log';
          this.toastrMsgService.error(msg);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  openAddWorkDialog(): void {
    const dialogRef = this.dialog.open(WorkDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      disableClose: true,
      data: { project_id: this.projectId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.getProjetWork();
      }
    });
  }

  openImageDialog(imageUrl: string): void {
    this.dialog.open(ImageViewDialogComponent, {
      data: { url: imageUrl },
      width: '600px',
      maxWidth: '95vw',
    });
  }

  refresh(): void {
    this.getProjetWork();
  }
}
